/**
 * One palette, one type scale, one set of seat identities.
 *
 * Everything drawn — on the canvas and in the DOM — resolves its
 * colour here, so "the ember orange in the ticket fuse" and "the ember orange
 * on a burning stove" are the same value rather than two similar hex codes that
 * will drift. The CSS mirrors these names in `index.css`; this file is the
 * canvas half, which cannot read custom properties.
 *
 * The direction is *the pass*: a dark, warm service kitchen seen from above,
 * with the order dockets clipped to a rail down its left edge on cream paper.
 * The room is walnut, slate and soot and the paper is the only bright surface,
 * which is what makes a ticket the first thing the eye lands on.
 *
 * Two rules constrain every value below.
 *
 *  - **No meaning in colour alone.** Every colour that carries meaning is
 *    paired with a shape, a motion or a mark somewhere in `icons.ts`,
 *    `stations.ts` or `hud.tsx`. The tables here note which.
 *  - **Contrast is checked.** Text colours are chosen against the surface they
 *    sit on, not against the page background; the ratios are noted where they
 *    are close.
 */

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export const COLOURS = {
  /** Page background. Warm near-black — soot, not blue-grey. */
  ink: '#12100e',
  /** Raised panels on the page. */
  ink2: '#1c1815',
  /** Hairlines and inactive tracks. */
  rule: '#2e2823',

  /**
   * The kitchen floor: a warm sand tile set in a slightly darker grout, so dark
   * objects on it read and the grid of cells is visible without a drawn grid.
   */
  floor: '#ece0c3',
  floorGrout: '#e3d6b6',
  floorLine: 'rgba(150, 128, 92, 0.35)',
  /** The room's boundary. Deliberately close to the page so the map floats. */
  wall: '#221d19',
  /** A wall tile: a soot face inside a darker edge. */
  wallFace: '#201914',
  wallEdge: '#140f0c',
  /**
   * Walnut counter tops — the surface most of the kitchen is made of. A lit
   * lip along the top, a shadow along the front, and a dark edge between
   * neighbouring tiles so a run of stations reads as one bench in sections.
   */
  counter: '#a97a47',
  counterEdge: '#7d5530',
  counterLip: '#c69158',
  counterShadow: 'rgba(52, 32, 16, 0.3)',
  /** Brushed steel: stove tops, sinks, the oven face. */
  steel: '#5c6570',
  steelDark: '#3b424b',
  steelLight: '#8d99a6',

  /** Ticket stock, and every card on every screen. */
  paper: '#f6efdf',
  paperEdge: '#d8c8a8',
  paperInk: '#2a2118',
  paperInkSoft: '#7a6a55',

  // -- accents. Each one has exactly one job. --------------------------------
  /** Urgency, fire, and nothing else. */
  ember: '#f2662c',
  emberHot: '#ff9245',
  /** Loss: a ticket run out, an action refused. */
  blood: '#d93b2b',
  /** Score, and the "this is the thing to look at" highlight. */
  brass: '#f2b544',
  /** Good: a full fuse, a cook in progress, a served plate. */
  mint: '#4cc38a',
  /** Cool counterpoint — water, clean plates, seat two. */
  ice: '#5aa9e6',

  /** Text on dark surfaces. */
  text: '#f0e9dd',
  textSoft: '#a89a86',
} as const

// ---------------------------------------------------------------------------
// Type
//
// No font may be fetched — the box has no CDN allowance and the game must work
// offline — so these are stacks of faces that already exist on the machines a
// grant panel uses, chosen to be anything but the default UI sans.
// ---------------------------------------------------------------------------

/** Industrial condensed: Windows ships Bahnschrift, macOS ships DIN Alternate. */
const FONT_DISPLAY =
  "Bahnschrift, 'DIN Alternate', 'DIN Condensed', 'Franklin Gothic Medium', 'Nimbus Sans Narrow', 'Arial Narrow', sans-serif"

/** A canvas font shorthand, since `ctx.font` takes a string and nothing else. */
export const font = (weight: number, px: number, stack: string = FONT_DISPLAY): string =>
  `${weight} ${Math.round(px)}px ${stack}`

// ---------------------------------------------------------------------------
// Seats
//
// Seat is ordering, never identity (the engine has no `A`/`B`), but two chefs
// on one screen have to be told apart instantly. Each seat therefore carries
// four independent cues: a colour, a *build*, an apron cut and a hat. A player
// who cannot see the difference between the red and the blue can still see that
// one chef is square-shouldered in a tall toque and the other is round in a
// flat cap — and `silhouette` gives them the words for it, which the HUD roster
// prints beside the drawing so the vocabulary is learned rather than guessed.
// ---------------------------------------------------------------------------

type ApronShape = 'square' | 'round' | 'notched' | 'pointed'
type HatShape = 'toque' | 'cap' | 'bandana' | 'beanie'
/** Square shoulders or a round barrel: the seat cue that survives greyscale. */
export type Build = 'broad' | 'slight'

export interface SeatIdentity {
  colour: string
  /** A darker shade of the same hue, for the outline and the apron shadow. */
  shade: string
  build: Build
  apron: ApronShape
  hat: HatShape
  /** Spoken name for the seat, used in the lobby and on the end screen. */
  label: string
  /**
   * What two strangers on a voice call will actually call this chef. Shown in
   * the HUD roster beside the drawing, so the vocabulary is taught rather than
   * assumed — and it names the *shape*, which is the cue that works for a
   * colourblind player.
   */
  silhouette: string
}

export const SEATS: SeatIdentity[] = [
  {
    colour: '#e8543f',
    shade: '#a3341f',
    build: 'broad',
    apron: 'square',
    hat: 'toque',
    label: 'Red',
    silhouette: 'tall hat',
  },
  {
    colour: '#4a9fe0',
    shade: '#2a6394',
    build: 'slight',
    apron: 'round',
    hat: 'cap',
    label: 'Blue',
    silhouette: 'flat cap',
  },
  {
    colour: '#63c96e',
    shade: '#31803c',
    build: 'broad',
    apron: 'notched',
    hat: 'bandana',
    label: 'Green',
    silhouette: 'bandana',
  },
  {
    colour: '#c98ae0',
    shade: '#7d4a93',
    build: 'slight',
    apron: 'pointed',
    hat: 'beanie',
    label: 'Violet',
    silhouette: 'beanie',
  },
]

export const seatIdentity = (seat: number): SeatIdentity =>
  SEATS[(Math.max(1, seat) - 1) % SEATS.length]

// ---------------------------------------------------------------------------
// Small canvas helpers used by every drawing module
// ---------------------------------------------------------------------------

/**
 * A rounded rectangle path. `ctx.roundRect` is too new to rely on everywhere.
 *
 * Corners are true circular arcs (`arcTo`). A negative width or height is
 * normalised to the same box measured the usual way first, and the radius is
 * clamped to half of each side.
 */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  // A box measured right-to-left or bottom-to-top is the same box. Normalising
  // first keeps every corner turning the same way, so `arcTo` never has to
  // bridge a corner with a stray straight segment.
  if (w < 0) {
    x += w
    w = -w
  }
  if (h < 0) {
    y += h
    h = -h
  }
  const radius = Math.max(0, Math.min(r, w / 2, h / 2))
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

/** Blend two `#rrggbb` colours. One expression, used by every ramp. */
export function mixHex(from: string, to: string, t: number): string {
  const clamp = Math.min(1, Math.max(0, t))
  const parse = (hex: string): [number, number, number] => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ]
  const [ar, ag, ab] = parse(from)
  const [br, bg, bb] = parse(to)
  const channel = (a: number, b: number): string =>
    Math.round(a + (b - a) * clamp)
      .toString(16)
      .padStart(2, '0')
  return `#${channel(ar, br)}${channel(ag, bg)}${channel(ab, bb)}`
}

/**
 * Darken or lighten a colour by a fraction. Used for the one-light-source
 * shading every tile and icon shares: light from the top-left, shadow below.
 */
export const shade = (hex: string, amount: number): string =>
  amount < 0 ? mixHex(hex, '#000000', -amount) : mixHex(hex, '#ffffff', amount)
